import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, Loader2, Pencil, RotateCcw, Send, Settings2, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import { HelpHint } from './HelpHint';

const PRIORITIES = ['P1', 'P2', 'P3', 'P4'] as const;

/**
 * Editing, archiving and restoring a ticket.
 *
 * The API has had `PATCH /tickets/:id` and `POST /tickets/:id/archive` since
 * Phase 5, and no screen ever called either — a dispatcher who logged a fault
 * against the wrong customer had no way to correct it, and a duplicate ticket
 * stayed on the board forever.
 *
 * Archiving is the everyday tool and restore sits next to it, which is what
 * makes it safe to press. Deleting is separate, admin-only, and appears only
 * once a ticket is already archived — and the API refuses it outright for
 * anything with photos, stock, an assignment or sent messages behind it,
 * because that is a record of real work and removing it would quietly shrink
 * the reports and stock figures the business is run on.
 */
export function ManageTicket({
  ticketId,
  current,
}: {
  ticketId: string;
  current: {
    title: string;
    description: string | null;
    priority: string;
    status: string;
    estDurationMin: number | null;
    areaId?: string | null;
    customerId?: string | null;
    archivedAt?: string | null;
    archivedReason?: string | null;
  };
}) {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({
    title: current.title,
    description: current.description ?? '',
    priority: current.priority,
    estDurationMin: current.estDurationMin ? String(current.estDurationMin) : '',
    areaId: current.areaId ?? '',
    customerId: current.customerId ?? '',
  });

  const mayEdit = can('ticket:update');
  const mayArchive = can('ticket:archive');
  const mayDelete = can('ticket:delete');

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
    void queryClient.invalidateQueries({ queryKey: ['tickets'] });
    void queryClient.invalidateQueries({ queryKey: ['ticket-actions', ticketId] });
  };

  const fail = (caught: Error) => setError(caught.message);

  const save = useMutation({
    mutationFn: () =>
      api.updateTicket(ticketId, {
        title: form.title,
        description: form.description,
        priority: form.priority,
        // Blank clears it; a number sets it. Sending "" would fail validation.
        ...(form.estDurationMin ? { estDurationMin: Number(form.estDurationMin) } : {}),
        areaId: form.areaId || null,
        customerId: form.customerId || null,
      }),
    onSuccess: () => {
      setError(null);
      setOpen(false);
      refresh();
    },
    onError: fail,
  });

  const archive = useMutation({
    mutationFn: () => api.archiveTicket(ticketId, reason),
    onSuccess: () => {
      setError(null);
      setReason('');
      refresh();
    },
    onError: fail,
  });

  const destroy = useMutation({
    mutationFn: () => api.deleteTicket(ticketId, deleteReason),
    onSuccess: () => {
      // The ticket no longer exists, so staying on its page would show a 404.
      void queryClient.invalidateQueries({ queryKey: ['tickets'] });
      navigate('/list');
    },
    onError: fail,
  });

  const restore = useMutation({
    mutationFn: () => api.restoreTicket(ticketId),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: fail,
  });

  const submitDraft = useMutation({
    mutationFn: () => api.transition(ticketId, 'submit', {}),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: fail,
  });

  // Wrapped rather than passed directly: api.areas takes a search string, and
  // TanStack would hand it the query context as that argument.
  const areas = useQuery({ queryKey: ['areas'], queryFn: () => api.areas(), enabled: open });
  const customers = useQuery({ queryKey: ['customers'], queryFn: api.customers, enabled: open });

  const archived = Boolean(current.archivedAt);

  /*
   * Rendered nothing, but only after every hook above has run.
   *
   * This used to return early, before the mutations. That is a crash waiting
   * for the moment somebody's capabilities change while they are on the page —
   * an admin granting them a role, or the session refetching — because the
   * component would go from seven hooks to none between two renders and React
   * reads hook state positionally. "Rendered fewer hooks than expected" takes
   * the whole ticket screen down, and the person who caused it is on a
   * different screen entirely.
   *
   * Hooks first, decisions after. Always.
   */
  if (!mayEdit && !mayArchive && !mayDelete) return null;

  return (
    <section className="card">
      <h3 className="mb-1 flex items-center gap-1.5 font-heading text-lg">
        <Settings2 className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        Manage
        <HelpHint>
          Nothing here is deleted. Archiving hides a ticket from every list and keeps its history,
          so the SLA report stays accurate — and you can bring it back.
        </HelpHint>
      </h3>

      {archived && (
        <div className="mb-4 rounded-xl bg-muted p-3 text-sm">
          <p className="font-medium">This ticket is archived.</p>
          {current.archivedReason && (
            <p className="mt-0.5 text-muted-foreground">{current.archivedReason}</p>
          )}
          {mayArchive && (
            <button
              type="button"
              className="btn-secondary mt-3"
              disabled={restore.isPending}
              onClick={() => restore.mutate()}
            >
              {restore.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <RotateCcw className="h-4 w-4" aria-hidden="true" />
              )}
              Restore
            </button>
          )}
        </div>
      )}

      {/*
        Deletion lives here, behind the archive, for two reasons: it is only
        reachable once somebody has already taken a deliberate step, and the
        restore button sits right above it as the thing they probably wanted.

        The API refuses to delete a ticket with photos, stock, an assignment or
        sent messages — that is a record of real work, and removing it would
        quietly shrink the reports and stock figures the business runs on.
      */}
      {archived && mayDelete && (
        <div className="mt-4 rounded-xl border border-danger-200 bg-danger-50/40 p-3">
          <p className="text-sm font-medium text-danger-700">Delete permanently</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Only for a ticket that should never have existed — a test, or a duplicate typed twice.
            A ticket with photos, stock or a technician on it cannot be deleted; leave that
            archived. This cannot be undone.
          </p>

          {!confirmingDelete ? (
            <button
              type="button"
              className="btn-secondary mt-3 text-danger-600"
              onClick={() => setConfirmingDelete(true)}
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
              Delete permanently
            </button>
          ) : (
            <div className="mt-3 space-y-2">
              <label className="block">
                <span className="mb-1 block text-xs font-medium">
                  Type <span className="font-mono">DELETE</span> to confirm
                </span>
                <input
                  className="input w-full"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  autoComplete="off"
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium">Why</span>
                <input
                  className="input w-full"
                  value={deleteReason}
                  placeholder="Test ticket created while checking the form"
                  onChange={(e) => setDeleteReason(e.target.value)}
                />
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="btn-secondary text-danger-600"
                  disabled={
                    confirmText !== 'DELETE' || deleteReason.trim().length < 3 || destroy.isPending
                  }
                  onClick={() => destroy.mutate()}
                >
                  {destroy.isPending && (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  )}
                  Delete {current.title.slice(0, 24)}
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setConfirmingDelete(false);
                    setConfirmText('');
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* A draft has never been sent to anyone and its SLA clocks are stopped.
          Submitting is the moment the promise to the customer starts. */}
      {current.status === 'DRAFT' && !archived && mayEdit && (
        <div className="mb-4 rounded-xl bg-accent-50 p-3 text-sm">
          <p className="font-medium text-accent-700">This is a draft.</p>
          <p className="mt-0.5 text-muted-foreground">
            Nobody has been notified and the SLA clock has not started. Submitting begins both.
          </p>
          <button
            type="button"
            className="btn-primary mt-3"
            disabled={submitDraft.isPending}
            onClick={() => submitDraft.mutate()}
          >
            {submitDraft.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Send className="h-4 w-4" aria-hidden="true" />
            )}
            Submit ticket
          </button>
        </div>
      )}

      {!archived && mayEdit && !open && (
        <button type="button" className="btn-secondary" onClick={() => setOpen(true)}>
          <Pencil className="h-4 w-4" aria-hidden="true" />
          Edit details
        </button>
      )}

      {!archived && mayEdit && open && (
        <div className="space-y-4">
          <label className="block">
            <span className="mb-1 block text-sm font-medium">Title</span>
            <input
              className="input w-full"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-sm font-medium">Description</span>
            <textarea
              className="input w-full"
              rows={3}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
          </label>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-sm font-medium">Priority</span>
              <select
                className="input w-full"
                value={form.priority}
                onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
              >
                {PRIORITIES.map((priority) => (
                  <option key={priority} value={priority}>
                    {priority}
                  </option>
                ))}
              </select>
              <span className="mt-1 block text-xs text-muted-foreground">
                Changing this changes the SLA target the ticket is measured against.
              </span>
            </label>

            <label className="block">
              <span className="mb-1 block text-sm font-medium">Expected duration (minutes)</span>
              <input
                type="number"
                min={5}
                max={2880}
                className="input w-full"
                value={form.estDurationMin}
                onChange={(e) => setForm((f) => ({ ...f, estDurationMin: e.target.value }))}
              />
            </label>

            <label className="block">
              <span className="mb-1 block text-sm font-medium">Area</span>
              <select
                className="input w-full"
                value={form.areaId}
                onChange={(e) => setForm((f) => ({ ...f, areaId: e.target.value }))}
              >
                <option value="">Not set</option>
                {(areas.data?.areas ?? []).map((area) => (
                  <option key={area.id} value={area.id}>
                    {area.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="mb-1 block text-sm font-medium">Customer</span>
              <select
                className="input w-full"
                value={form.customerId}
                onChange={(e) => setForm((f) => ({ ...f, customerId: e.target.value }))}
              >
                <option value="">Not set</option>
                {(customers.data?.customers ?? []).map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              className="btn-primary"
              disabled={save.isPending}
              onClick={() => save.mutate()}
            >
              {save.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              Save
            </button>
            <button type="button" className="btn-secondary" onClick={() => setOpen(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {!archived && mayArchive && (
        <div className="mt-5 border-t border-border pt-4">
          <label className="block">
            <span className="mb-1 block text-sm font-medium">Archive this ticket</span>
            <span className="mb-2 block text-xs text-muted-foreground">
              For a duplicate, a test, or one logged in error. Say why — it stays on the record.
            </span>
            <input
              className="input w-full"
              value={reason}
              placeholder="Duplicate of JOB-1042"
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          <button
            type="button"
            className="btn-secondary mt-2 text-danger-600"
            disabled={reason.trim().length < 3 || archive.isPending}
            onClick={() => archive.mutate()}
          >
            {archive.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Archive className="h-4 w-4" aria-hidden="true" />
            )}
            Archive
          </button>
        </div>
      )}

      {error && <p className="mt-4 text-sm text-danger-600">{error}</p>}
    </section>
  );
}
